import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  IUserInfoDTO,
  ISystemSettingsDTO,
  IPreferencesDTO,
  IUpdatePreferencesRequestDTO,
  IAuthRequestDTO,
  ISSOAuthRequestDTO,
} from 'src/api/dtos';
import AuthRepository from 'src/api/repositories/common/v1/auth.repository';
import UserInfoRepository from 'src/api/repositories/common/v1/userInfo.repository';
import PreferencesRepository from 'src/api/repositories/ess/v1/preferences.repository';
import SystemSettingsRepository from 'src/api/repositories/ess/v1/settings.repository';
import { LocalStorageEnums } from 'src/shared/enums';
import { clearLocalStorageValues, encrypt, mapLanguageIdEnums } from 'src/shared/utils';

import { setAutoAuthenticatedActionCreator } from './user.slice';
import { UserDispatch } from './user.types';

export const authenticate = createAsyncThunk<boolean, IAuthRequestDTO>(
  'user/authenticate',
  async (payload: IAuthRequestDTO, { rejectWithValue }) => {
    try {
      clearLocalStorageValues([LocalStorageEnums.THEME_MODE]);
      const repository = new AuthRepository();
      await repository.authenticate(payload);

      // BFF Pattern: Backend sets auth_token HttpOnly cookie automatically
      // response.token is null for web clients — do not store in localStorage

      return true;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const ssoAuthenticate = createAsyncThunk<boolean, ISSOAuthRequestDTO & { region?: string }>(
  'user/sso-authenticate',
  async ({ region, ...payload }, { rejectWithValue }) => {
    try {
      clearLocalStorageValues([LocalStorageEnums.THEME_MODE]);

      // set app region
      if (region) {
        localStorage.setItem(LocalStorageEnums.APP_REGION, region);
      }

      const repository = new AuthRepository();
      const response = await repository.ssoAuthenticate(payload);

      localStorage.setItem(LocalStorageEnums.TOKEN_KEY, JSON.stringify(response.token));
      localStorage.setItem(
        LocalStorageEnums.REFRESH_TOKEN_KEY,
        JSON.stringify(encrypt(response.refreshToken, LocalStorageEnums.REFRESH_TOKEN_KEY)),
      );

      return true;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchUserInfo = createAsyncThunk<IUserInfoDTO, void>(
  'user/get-userInfo',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new UserInfoRepository();
      return await repository.getUserInfo();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const updateUserInfo = createAsyncThunk<
  IUserInfoDTO,
  { email: string | undefined; cellPhone: string | undefined }
>('user/update-userInfo', async (payload, { rejectWithValue }) => {
  try {
    const repository = new UserInfoRepository();
    return await repository.updateUserInfo(payload);
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const fetchSystemSettings = createAsyncThunk<{ data: ISystemSettingsDTO; apiVersion: string }, void>(
  'user/systemSettings',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new SystemSettingsRepository();
      return await repository.get();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchPreferences = createAsyncThunk<IPreferencesDTO, void>(
  'user/preferences',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new PreferencesRepository();
      const response = await repository.get();

      localStorage.setItem(LocalStorageEnums.LANGUAGE_KEY, JSON.stringify(mapLanguageIdEnums[response.languageId]));

      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchDefaultPreferences = createAsyncThunk<IPreferencesDTO, void>(
  'user/default-preferences',
  async (_, { rejectWithValue }) => {
    try {
      const repository = new PreferencesRepository();
      return await repository.getDefault();
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const updatePreferences = createAsyncThunk<IPreferencesDTO, IUpdatePreferencesRequestDTO>(
  'user/update-preferences',
  async (payload, { rejectWithValue }) => {
    try {
      const repository = new PreferencesRepository();
      const response = await repository.patch(payload);

      if (payload.languageId) {
        localStorage.setItem(LocalStorageEnums.LANGUAGE_KEY, JSON.stringify(mapLanguageIdEnums[response.languageId]));
      }

      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const logout = createAsyncThunk<void, void>('user/logout', async (_, { rejectWithValue }) => {
  try {
    const repository = new AuthRepository();
    await repository.logout();

    clearLocalStorageValues([LocalStorageEnums.THEME_MODE]);
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const setAutoAuthenticated = () => {
  return (dispatch: UserDispatch): void => {
    dispatch(setAutoAuthenticatedActionCreator());
  };
};
